import axios from 'axios';

// Set the base URL for Strapi API
// In production, you would use an environment variable
const API_URL = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337';

// Helper function to construct safe URLs for media/assets
export const getStrapiMediaUrl = (url) => {
  if (!url) return null;

  // If the URL is absolute (starts with http), return it as is
  if (url.startsWith('http')) {
    return url;
  }

  // Otherwise, prefix it with the API URL, ensuring the path is correct
  return `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Create an axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the token in authenticated requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('strapiToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Authentication services
export const authService = {
  login: async (identifier, password) => {
    const response = await axios.post(`${API_URL}/auth/local`, {
      identifier,
      password,
    });
    if (response.data.jwt) {
      localStorage.setItem('strapiToken', response.data.jwt);
      localStorage.setItem('strapiUser', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('strapiToken');
    localStorage.removeItem('strapiUser');
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('strapiUser');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('strapiToken');
  },
};

// Announcements services
export const announcementService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/api/announcements', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching announcements:', error);

      // Check if this is a permission error
      if (error.response && error.response.status === 403) {
        console.error('Permission denied. Make sure Public role has access to Announcements in Strapi Settings → Users & Permissions Plugin → Roles → Public');
      }

      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/api/announcements/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching announcement ${id}:`, error);
      throw error;
    }
  },

  create: async (data) => {
    const response = await api.post('/api/announcements', { data });
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/api/announcements/${id}`, { data });
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/api/announcements/${id}`);
    return response.data;
  },
};

// Photos services
export const photoService = {
  getAll: async (params = {}) => {
    const response = await api.get('/photos', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/photos/${id}`);
    return response.data;
  },
};

// Documents services
export const documentService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/api/site-documents', {
        params: {
          ...params,
          populate: '*',
          sort: 'displayOrder:asc'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/api/site-documents/${id}`, {
        params: {
          populate: '*'
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching document ${id}:`, error);
      throw error;
    }
  },

  getByCategory: async (category) => {
    try {
      const response = await api.get('/api/site-documents', {
        params: {
          'filters[category][$eq]': category,
          'sort': 'displayOrder:asc',
          'populate': '*'
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching documents for category ${category}:`, error);
      throw error;
    }
  }
};

// Links services
export const linkService = {
  getAll: async (params = {}) => {
    const response = await api.get('/links', { params });
    return response.data;
  },
};

// Board members services
export const boardMemberService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/api/board-members', { params });

      // Return the data directly so components can access the correct structure
      return response.data;
    } catch (error) {
      console.error('Error in boardMemberService.getAll():', error);
      throw error;
    }
  },
};

// Real estate info services
export const realEstateService = {
  getAll: async (params = {}) => {
    const response = await api.get('/real-estate-listings', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/real-estate-listings/${id}`);
    return response.data;
  },
};

// Events services
export const eventService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/api/events', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/api/events/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching event ${id}:`, error);
      throw error;
    }
  },
};

// Contact form submission
export const submitContactForm = async (formData) => {
  try {
    const response = await api.post('/api/contact-messages', {
      data: formData
    });

    // Always return success if we get here without an error
    return {
      success: true,
      data: response.data || {},
      message: 'Message sent successfully'
    };
  } catch (error) {
    console.error('[API] Error submitting contact form:', error);

    // Format error message properly
    let errorMessage = 'Failed to submit the contact form. Please try again later.';

    if (error.response?.status === 403) {
      errorMessage = 'Permission denied. Please ask the administrator to configure proper permissions in Strapi.';
    } else if (error.response?.data?.error) {
      const strapiError = error.response.data.error;
      if (typeof strapiError === 'string') {
        errorMessage = strapiError;
      } else if (strapiError && typeof strapiError.message === 'string') {
        errorMessage = strapiError.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

// Get board member recipients
export const getBoardMembers = async () => {
  try {
    console.log('Fetching board members...');
    // Use the standard API endpoint
    const response = await api.get('/api/board-members', {
      params: {
        'filters[active][$eq]': true,
        sort: 'order:asc',
        populate: '*'
      }
    });

    console.log('API response data structure:', JSON.stringify(response.data, null, 2));

    // Check if response has the expected format
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }

    console.warn('Unexpected response format from board-members API:', response);
    return [];
  } catch (error) {
    console.error('Error fetching board members:', error);
    // If we got a 403 error, log a more helpful message
    if (error.response?.status === 403) {
      console.error('Permission denied. Make sure Public role has access to Board Members in Strapi Settings → Users & Permissions Plugin → Roles → Public');
    } else if (error.response?.status === 404) {
      console.error('Board member API endpoint not found. Please verify the Strapi API configuration.');
    }

    // Return empty array instead of throwing
    return [];
  }
};

// Resident contact information services
export const residentContactService = {
  createOrUpdate: async (contactData) => {
    try {
      const response = await api.post('/api/resident-contacts/create-or-update', {
        data: contactData
      });

      return {
        success: true,
        data: response.data,
        message: response.data.message || 'Contact information updated successfully'
      };
    } catch (error) {
      console.error('[API] Error updating resident contact:', error);

      let errorMessage = 'Failed to update contact information. Please try again later.';

      if (error.response?.status === 403) {
        errorMessage = 'Permission denied. Please ask the administrator to configure proper permissions in Strapi.';
      } else if (error.response?.data?.error) {
        const strapiError = error.response.data.error;
        if (typeof strapiError === 'string') {
          errorMessage = strapiError;
        } else if (strapiError && typeof strapiError.message === 'string') {
          errorMessage = strapiError.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  },

  getByEmail: async (email) => {
    try {
      const response = await api.get('/api/resident-contacts', {
        params: {
          'filters[email][$eq]': email
        }
      });

      if (response.data && response.data.data && response.data.data.length > 0) {
        return {
          success: true,
          data: response.data.data[0]
        };
      }

      return {
        success: false,
        message: 'No contact information found for this email'
      };
    } catch (error) {
      console.error('[API] Error fetching resident contact:', error);
      return {
        success: false,
        error: 'Failed to retrieve contact information'
      };
    }
  }
};

// ARC request service
export const arcRequestService = {
  submitRequest: async (formData, files) => {
    try {
      // Create FormData object for file upload
      const requestFormData = new FormData();

      // Prepare the data object with request details
      const requestData = {
        ...formData,
        requestDate: new Date().toISOString(),
        status: 'pending'
      };

      // Add the data to FormData
      requestFormData.append('data', JSON.stringify(requestData));

      // Add files if any
      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          requestFormData.append(`files.supportingDocuments`, files[i], files[i].name);
        }
      }

      // Use raw axios to avoid Content-Type header conflict with FormData
      const response = await axios.post(`${API_URL}/api/arc-requests`, requestFormData);

      return {
        success: true,
        data: response.data,
        message: 'Your request has been submitted successfully'
      };
    } catch (error) {
      console.error('Error submitting ARC request:', error);

      let errorMessage = 'Failed to submit request. Please try again later.';

      if (error.response?.status === 403) {
        errorMessage = 'Permission denied. Please ask the administrator to configure proper permissions in Strapi.';
      } else if (error.response?.data?.error) {
        const strapiError = error.response.data.error;
        if (typeof strapiError === 'string') {
          errorMessage = strapiError;
        } else if (strapiError && typeof strapiError.message === 'string') {
          errorMessage = strapiError.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  },

  getAll: async (params = {}) => {
    try {
      const response = await api.get('/api/arc-requests', {
        params: {
          ...params,
          populate: '*'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching ARC requests:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/api/arc-requests/${id}`, {
        params: {
          populate: '*'
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching ARC request ${id}:`, error);
      throw error;
    }
  }
};

// Neighbor Business service
export const neighborBusinessService = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/api/neighbor-businesses', {
        params: {
          ...params,
          populate: '*',
          'filters[isActive][$eq]': true,
          sort: 'displayOrder:asc'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching neighbor businesses:', error);
      throw error;
    }
  },

  getByCategory: async (category) => {
    try {
      const response = await api.get('/api/neighbor-businesses', {
        params: {
          'filters[category][$eq]': category,
          'filters[isActive][$eq]': true,
          sort: 'displayOrder:asc',
          populate: '*'
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching businesses for category ${category}:`, error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/api/neighbor-businesses/${id}`, {
        params: {
          populate: '*'
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching neighbor business ${id}:`, error);
      throw error;
    }
  },

  // Submit a new business (marked as inactive for admin review)
  submitBusiness: async (formData, logoFile = null) => {
    try {
      const businessData = {
        ...formData,
        isActive: false, // Marked inactive until admin approves
        displayOrder: 999 // Default to end of list
      };

      // Step 1: Create the business entry
      const response = await api.post('/api/neighbor-businesses', { data: businessData });

      // Step 2: If there's a logo, upload it and link it to the entry
      if (logoFile && response.data?.data?.id) {
        const documentId = response.data.data.documentId;

        try {
          // First upload the file - use raw axios to avoid Content-Type header interference
          const uploadFormData = new FormData();
          uploadFormData.append('files', logoFile);

          const uploadResponse = await axios.post(`${API_URL}/api/upload`, uploadFormData);

          // Then link it to the business entry
          if (uploadResponse.data && uploadResponse.data[0]?.id) {
            const fileId = uploadResponse.data[0].id;
            await api.put(`/api/neighbor-businesses/${documentId}`, {
              data: { logo: fileId }
            });
          }
        } catch (uploadError) {
          console.error('Logo upload failed, but business was created:', uploadError);
          // Don't fail the whole submission if just the logo upload fails
        }
      }

      return {
        success: true,
        data: response.data,
        message: 'Your business has been submitted for review. It will appear in the directory once approved by the HOA board.'
      };
    } catch (error) {
      console.error('Error submitting business:', error);

      let errorMessage = 'Failed to submit business. Please try again later.';

      if (error.response?.status === 403) {
        errorMessage = 'Permission denied. Please contact the administrator.';
      } else if (error.response?.data?.error) {
        const strapiError = error.response.data.error;
        if (typeof strapiError === 'string') {
          errorMessage = strapiError;
        } else if (strapiError && typeof strapiError.message === 'string') {
          errorMessage = strapiError.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }
};

// Survey service
export const surveyService = {
  // Get all active surveys
  getActive: async () => {
    try {
      const now = new Date().toISOString();
      const response = await api.get('/api/surveys', {
        params: {
          'filters[isActive][$eq]': true,
          'filters[$or][0][endDate][$gte]': now,
          'filters[$or][1][endDate][$null]': true,
          populate: {
            questions: {
              sort: ['displayOrder:asc']
            }
          },
          sort: 'createdAt:desc'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching active surveys:', error);
      throw error;
    }
  },

  // Get a single survey by ID with questions
  getById: async (id) => {
    try {
      // Use filters to find by numeric ID (Strapi 5 requires documentId for direct access)
      const response = await api.get('/api/surveys', {
        params: {
          'filters[id][$eq]': id,
          populate: {
            questions: {
              sort: ['displayOrder:asc']
            }
          }
        }
      });
      // Return the first result wrapped in data structure
      if (response.data?.data?.length > 0) {
        return { data: response.data.data[0] };
      }
      return { data: null };
    } catch (error) {
      console.error(`Error fetching survey ${id}:`, error);
      throw error;
    }
  },

  // Check if user already submitted (by sessionId for anonymous, or email for non-anonymous)
  checkExistingResponse: async (surveyId, sessionId, email = null, isAnonymous = true) => {
    try {
      // For anonymous surveys, check by sessionId
      if (isAnonymous) {
        const response = await api.get('/api/survey-responses', {
          params: {
            'filters[survey][id][$eq]': surveyId,
            'filters[sessionId][$eq]': sessionId
          }
        });
        return {
          exists: response.data?.data?.length > 0,
          reason: 'session'
        };
      }

      // For non-anonymous surveys, check by email (primary) and sessionId (secondary)
      const emailResponse = await api.get('/api/survey-responses', {
        params: {
          'filters[survey][id][$eq]': surveyId,
          'filters[respondentEmail][$eq]': email
        }
      });

      if (emailResponse.data?.data?.length > 0) {
        return { exists: true, reason: 'email' };
      }

      // Also check sessionId as backup
      const sessionResponse = await api.get('/api/survey-responses', {
        params: {
          'filters[survey][id][$eq]': surveyId,
          'filters[sessionId][$eq]': sessionId
        }
      });

      if (sessionResponse.data?.data?.length > 0) {
        return { exists: true, reason: 'session' };
      }

      return { exists: false, reason: null };
    } catch (error) {
      console.error('Error checking existing response:', error);
      return { exists: false, reason: null };
    }
  },

  // Submit a survey response
  submitResponse: async (surveyId, answers, respondentInfo = {}, sessionId) => {
    try {
      // First, create the survey response
      const responseData = {
        survey: surveyId,
        sessionId: sessionId,
        submittedAt: new Date().toISOString(),
        respondentName: respondentInfo.name || '',
        respondentEmail: respondentInfo.email || ''
      };

      const surveyResponse = await api.post('/api/survey-responses', {
        data: responseData
      });

      const responseId = surveyResponse.data?.data?.id;

      if (!responseId) {
        throw new Error('Failed to create survey response');
      }

      // Then, create each answer
      const answerPromises = Object.entries(answers).map(([questionId, answerValue]) => {
        return api.post('/api/survey-answers', {
          data: {
            response: responseId,
            question: parseInt(questionId),
            answerValue: typeof answerValue === 'object' ? JSON.stringify(answerValue) : String(answerValue)
          }
        });
      });

      await Promise.all(answerPromises);

      return {
        success: true,
        message: 'Survey submitted successfully'
      };
    } catch (error) {
      console.error('Error submitting survey response:', error);

      let errorMessage = 'Failed to submit survey. Please try again later.';

      if (error.response?.status === 403) {
        errorMessage = 'Permission denied. Please contact the administrator.';
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }
};

// Generate a simple session ID for duplicate prevention
export const generateSessionId = () => {
  const stored = localStorage.getItem('surveySessionId');
  if (stored) return stored;

  const newId = 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
  localStorage.setItem('surveySessionId', newId);
  return newId;
};

export default api; 